"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import { UserAlreadyExistsError } from "@nelo/signup-lib/dist/utils/Errors"
import React, { useEffect, useState } from "react"

import TextInput from "@modules/signup-and-order-flow/components/common/TextInput"
import { VERIFICATION_CODE_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"
import { sendExperimentInclusionEvent } from "@modules/signup-and-order-flow/experiments/send-experiment-event"

import AppDownloadStep from "./AppDownloadStep"
import PinEntryStep from "./PinEntryStep"
import StepWrapper from "../../common/StepWrapper"

const VerificationCodeStep: React.FC<StepComponentProps> = ({
  onComplete,
  progressPercentage,
  onBack,
  onClose,
  stepId,
  treatments,
  errorHandler,
}) => {
  useEffect(() => {
    sendExperimentInclusionEvent("mktp_pin_entry_user_exists", treatments.mktp_pin_entry_user_exists)
  }, [treatments.mktp_pin_entry_user_exists])

  const verificationCodeText = VERIFICATION_CODE_STEP_TEXTS

  const { verificationCode, submitVerificationCode } = useSignup()
  const [showPinEntry, setShowPinEntry] = useState(false)
  const [showAppDownload, setShowAppDownload] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    verificationCode.setTouched(true)
    verificationCode.setBlurred(true)
    if (!verificationCode.isValid) {
      return
    }

    try {
      await submitVerificationCode.execute()
      onComplete()
    } catch (error) {
      console.error("Error verifying code:", error)

      if (error instanceof UserAlreadyExistsError) {
        datadogRum.addError(error, {
          step: stepId,
          action: "submitVerificationCode",
          errorType: "UserAlreadyExists",
        })
        if (treatments.mktp_pin_entry_user_exists.config) {
          setShowPinEntry(true)
        } else {
          setShowAppDownload(true)
        }
      } else {
        datadogRum.addError(error, {
          step: stepId,
          action: "submitVerificationCode",
        })
      }
    }
  }

  if (showPinEntry) {
    return (
      <PinEntryStep
        onComplete={onComplete}
        progressPercentage={progressPercentage}
        onBack={onBack}
        onClose={onClose}
        stepId={stepId}
        errorHandler={errorHandler}
        treatments={treatments}
      />
    )
  }

  if (showAppDownload) {
    return <AppDownloadStep onClose={onClose} />
  }

  return (
    <StepWrapper
      title={verificationCodeText.title}
      subtitle={verificationCodeText.subtitle}
      progressPercentage={progressPercentage}
      isLoading={submitVerificationCode.isLoading}
      formError={submitVerificationCode.error?.message}
      onSubmit={handleSubmit}
      submitDisabled={!verificationCode.isValid || submitVerificationCode.isLoading}
      submitButtonText={verificationCodeText.buttonText}
      showBackButton={true}
      onBack={onBack}
      footerText={verificationCodeText.footerText}
    >
      <div>
        <TextInput
          label={verificationCodeText.verificationCodeLabel}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={verificationCode.value}
          onChange={(e) => verificationCode.setValue(e.target.value)}
          onBlur={() => {
            verificationCode.setTouched(true)
            verificationCode.setBlurred(true)
            if (verificationCode.errorMessage) {
              datadogRum.addError(new Error(`Verification code validation error: ${verificationCode.errorMessage}`), {
                step: stepId,
                field: "verificationCode",
              })
            }
          }}
          onFocus={() => verificationCode.setTouched(true)}
          isValid={verificationCode.isValid}
          touched={verificationCode.touched}
          blurred={verificationCode.blurred}
          errorMessage={verificationCode.errorMessage}
        />
      </div>
    </StepWrapper>
  )
}

export default VerificationCodeStep
